"use client"

import { ArrowRight, BookOpen } from "lucide-react"
import Link from "next/link"

import { Badge } from "@/core/ui/badge"
import { blogPosts } from "@/data/blog"

interface RelatedContentForServiceProps {
    serviceId: string
    categoryId: string
}

// Map service categories to blog topics
const serviceToBlogTopics: Record<string, string[]> = {
    blockchain: ["blockchain", "smart contract", "defi", "web3"],
    genai: ["ai", "genai", "machine learning", "llm"],
    development: ["web", "development", "scalability", "engineering"],
    enterprise: ["enterprise", "cloud", "digital transformation", "technical debt"],
}

// Get related blog posts for a service
function getRelatedBlogPosts(categoryId: string) {
    const topics = serviceToBlogTopics[categoryId] || []

    return blogPosts
        .filter((post) => {
            const tagsLower = post.tags.map((t) => t.toLowerCase())
            const categoryLower = post.category.toLowerCase()
            const titleLower = post.title.toLowerCase()

            return topics.some((topic) =>
                tagsLower.some((tag) => tag.includes(topic)) ||
                categoryLower.includes(topic) ||
                titleLower.includes(topic)
            )
        })
        .slice(0, 3)
}

export function RelatedContentForService({ serviceId: _serviceId, categoryId }: RelatedContentForServiceProps) {
    const relatedBlogPosts = getRelatedBlogPosts(categoryId)

    if (relatedBlogPosts.length === 0) {
        return null
    }

    return (
        <section className="mt-20 pt-12 border-t border-border/30">
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h2 className="text-2xl font-bold tracking-tight mb-2">Related Insights</h2>
                    <p className="text-muted-foreground">Expert perspectives on this topic</p>
                </div>
                <Link
                    href="/blog"
                    className="hidden md:flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
                >
                    View All Insights <ArrowRight className="h-4 w-4" />
                </Link>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
                {relatedBlogPosts.map((post) => (
                    <Link
                        key={post.id}
                        href={post.slug}
                        className="group p-6 rounded-2xl border border-border/50 bg-card/20 hover:bg-card/40 hover:border-primary/30 transition-all duration-300"
                    >
                        <div className="flex items-center gap-2 mb-4">
                            <BookOpen className="h-4 w-4 text-primary" />
                            <Badge variant="outline" className="text-xs">
                                {post.category}
                            </Badge>
                        </div>
                        <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors mb-2 line-clamp-2">
                            {post.title}
                        </h3>
                        <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
                            {post.excerpt}
                        </p>
                        <span className="inline-flex items-center text-sm text-primary/80 font-medium group-hover:gap-2 transition-all">
                            Read More <ArrowRight className="h-3 w-3 ml-1" />
                        </span>
                    </Link>
                ))}
            </div>

            <div className="md:hidden mt-6 text-center">
                <Link
                    href="/blog"
                    className="inline-flex items-center gap-2 text-sm font-medium text-primary"
                >
                    View All Insights <ArrowRight className="h-4 w-4" />
                </Link>
            </div>
        </section>
    )
}
